import {
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Stack,
    Typography,
} from '@mui/material'
import { FeatureFlagIdAccessRequest, Features } from 'c1gen/features'
import { C1Icon } from 'components/core/icons/C1Icon'
import { createURL } from 'components/core/links/docs'
import { useFeatureCheckerLazy } from 'components/no-ui/capability'
import { useLocalStorage } from 'hooks/local-storage/index'
import {
    BookOpen,
    ClipboardCheck,
    ExternalLink,
    FileText,
    Gavel,
    LayoutGrid,
    Plus,
    Settings,
    Sparkles,
    Users,
    Waypoints,
} from 'lucide-react'
import { FC, ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { QuickLinksStyles } from './styles'

export const SPOTLIGHT_SEARCH_STORAGE_KEY = 'c1-spotlight-search'
export type SearchResult = {
    name: string,
    path: string,
}

type QuickLinkItemProps = {
    text: React.ReactElement | string
    icon?: React.ReactElement
    onClick: () => void
    features?: Array<Features>
}

const QuickLinkItem: FC<QuickLinkItemProps> = ({ text, icon, onClick, features }): ReactNode => {
    const hasFeature = useFeatureCheckerLazy()
    const hasFF = features?.length ? features.some(hasFeature) : true
    if (!hasFF) {
        return null
    }
    return <ListItem disablePadding>
        <ListItemButton onClick={onClick} sx={QuickLinksStyles.listItem} dense>
            {icon && <ListItemIcon sx={{ minWidth: 0 }}>{icon}</ListItemIcon>}
            <ListItemText>
                {typeof text === 'string'
                    ? <Typography noWrap title={text}>{text}</Typography>
                    : text
                }
            </ListItemText>
        </ListItemButton>
    </ListItem>
}

type QuickLinksProps = {
    goTo: (path: string) => void
}

export const QuickLinks: FC<QuickLinksProps> = ({ goTo }) => {
    const [recentSearches] = useLocalStorage<Array<SearchResult>>(SPOTLIGHT_SEARCH_STORAGE_KEY)
    const intl = useIntl()
    const docsTextElement =
        <Stack direction="row" spacing={1}>
            <Typography>
                <FormattedMessage id="documentation" defaultMessage="Documentation" />
            </Typography>
            <C1Icon
                icon={ExternalLink}
                role="img"
                aria-hidden="false"
                aria-label={intl.formatMessage({
                    id: 'component_sidebar_external_link',
                    defaultMessage: 'Open in new tab',
                })}
                size="sm"
            />
        </Stack>
    const quickActions: QuickLinkItemProps[] = [
        {
            text: <FormattedMessage id="request-access" defaultMessage="Request access" />,
            icon: <C1Icon icon={Plus} size="sm" />,
            onClick: (): void => goTo('/access-requests/create'),
            features: [FeatureFlagIdAccessRequest],
        },
        {
            text: <FormattedMessage id="new-campaign" defaultMessage="New campaign" />,
            icon: <C1Icon icon={ClipboardCheck} size="sm" />,
            onClick: (): void => goTo('/campaigns/create'),
        },
        {
            text: <FormattedMessage id="create-app" defaultMessage="Create application" />,
            icon: <C1Icon icon={LayoutGrid} size="sm" />,
            onClick: (): void => goTo('/admin/applications/create'),
        },
        {
            text: <FormattedMessage id="create-policy" defaultMessage="Create policy" />,
            icon: <C1Icon icon={Gavel} size="sm" />,
            onClick: (): void => goTo('/admin/settings/policies/create'),
        },
        {
            text: <FormattedMessage id="add-integration" defaultMessage="Add integration" />,
            icon: <C1Icon icon={Settings} size="sm" />,
            onClick: (): void => goTo('/integration'),
        },
    ]
    const navigateTo: QuickLinkItemProps[] = [
        {
            text: <FormattedMessage id="reviews" defaultMessage="Reviews" />,
            icon: <C1Icon icon={FileText} size="sm" />,
            onClick: (): void => goTo('/my_tasks/access_reviews'),
        },
        {
            text: <FormattedMessage id="campaigns" defaultMessage="Campaigns" />,
            icon: <C1Icon icon={ClipboardCheck} size="sm" />,
            onClick: (): void => goTo('/admin/campaigns/create'),
        },
        {
            text: <FormattedMessage id="connectors" defaultMessage="Connectors" />,
            icon: <C1Icon icon={Waypoints} size="sm" />,
            onClick: (): void => goTo('/admin/connectors'),
        },
        {
            text: <FormattedMessage id="users" defaultMessage="Users" />,
            icon: <C1Icon icon={Users} size="sm" />,
            onClick: (): void => goTo('/admin/directory/users'),
        },
        {
            text: docsTextElement,
            icon: <C1Icon icon={BookOpen} size="sm" />,
            onClick: () => window.open(createURL('/docs/product/intro/'), '_blank'),
        },
    ]

    return <Stack direction="row" alignItems="flex-start" justifyContent="space-around" pt={3}>
        <>
            <List
                subheader={
                    <ListSubheader sx={QuickLinksStyles.header}>
                        <FormattedMessage id="recent-searches" defaultMessage="Recent searches" />
                    </ListSubheader>
                }
                sx={QuickLinksStyles.section}
            >
                {recentSearches
                    ? recentSearches.map((search) => <QuickLinkItem
                        key={search.path}
                        text={search.name}
                        onClick={(): void => goTo(search.path)}
                    />)
                    : <ListItem>
                        <ListItemText>
                            <Stack direction="column" spacing={2} alignItems="center" pt={4}>
                                <C1Icon icon={Sparkles} />
                                <Typography sx={QuickLinksStyles.placeholder}>
                                    <FormattedMessage
                                        id="no-recent-searches"
                                        defaultMessage="Start searching. Your recent searches will appear here!"
                                    />
                                </Typography>
                            </Stack>
                        </ListItemText>
                    </ListItem>
                }
            </List>
            <Divider orientation="vertical" variant="middle" flexItem />
        </>
        <List
            subheader={
                <ListSubheader sx={QuickLinksStyles.header}>
                    <FormattedMessage id="quick-actions" defaultMessage="Quick actions" />
                </ListSubheader>
            }
            sx={QuickLinksStyles.section}
        >
            {quickActions.map((qa, i) => (
                <QuickLinkItem key={`qa-${i}`} text={qa.text} icon={qa.icon} onClick={qa.onClick} features={qa.features} />
            ))}
        </List>
        <Divider orientation="vertical" variant="middle" flexItem />
        <List
            subheader={
                <ListSubheader sx={QuickLinksStyles.header}>
                    <FormattedMessage id="navigate-to" defaultMessage="Navigate to" />
                </ListSubheader>
            }
            sx={QuickLinksStyles.section}
        >
            {navigateTo.map((nt, i) => (
                <QuickLinkItem key={`nt-${i}`} text={nt.text} icon={nt.icon} onClick={nt.onClick} features={nt.features} />
            ))}
        </List>
    </Stack>
}
